/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { Message, useTheme, Text, Modal } from "basis";
import { format } from "date-fns";
import { useProducts } from "library/hooks/useProducts";
import { getProductName } from "library/transforms";
import { ProgressCircle } from "../ProgressCircle/ProgressCircle";
import { CurrencyFormat } from "../CurrencyFormat";
import { SvgInline } from "../SvgInline/SvgInline";
import {
  PAYMENT_LOAN_SUPPORT,
  LINKING_LOAN_SUPPORT,
} from "../../constants/contacts";
import styles from "./personal-loan-account-summary.module.scss";

const RepaymentNotReceived = ({ countryCode = "AU" }) => {
  const [open, setOpen] = useState(true);
  const closeModal = () => setOpen(false);
  const contactInfo = PAYMENT_LOAN_SUPPORT[countryCode];

  return (
    <>
      <Modal
        title="It looks like you're behind on your repayments"
        open={open}
        onClose={closeModal}
      >
        <Text margin="4 0 0 0">
          Do you need our help? To discuss your account, the options we have to
          help you, or to advise of an upcoming payment.
        </Text>
        <Text margin="4 0 0 0">
          {`To arrange a repayment, please call ${contactInfo.NUM} Monday to Friday: ${contactInfo.HOURS_WEEKDAY}, Weekends: ${contactInfo.HOURS_WEEKEND}`}
        </Text>
        <Text margin="4 0 0 0">
          Note: A late fee may apply if you miss your monthly repayment.
        </Text>
        <Text margin="4 0 0 0">
          If you have recently brought your account up to date, please disregard
          this message.
        </Text>
      </Modal>
      <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
        {`Repayment was not received. Please call ${contactInfo.NUM} to arrange repayment.`}
      </Message>
    </>
  );
};

const FinalPayment = ({ date, countryCode }) => (
  <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
    {`Final repayment is due on ${date} of the month. Call ${PAYMENT_LOAN_SUPPORT[countryCode].NUM} to arrange final payment.`}
  </Message>
);

const RequestPayout = ({ countryCode }) => {
  const contactInfo = LINKING_LOAN_SUPPORT[countryCode] || {};

  return (
    <div className={`body02 ${styles.contact}`}>
      {`To request a payout figure, please call ${contactInfo.NUM}, Monday to Friday: ${contactInfo.HOURS_WEEKDAY}, Weekends: ${contactInfo.HOURS_WEEKEND}`}
    </div>
  );
};

const Progress = ({ percentage, accountClosed, loanBalance }) => (
  <div className={styles.progress}>
    <ProgressCircle
      diameter={175}
      strokeWidth={14}
      percentage={accountClosed ? 1 : percentage / 100}
      color={accountClosed ? "#1b633c" : "#0046aa"}
    />
    <div className={styles.progressInfo}>
      {accountClosed ? (
        <>
          <SvgInline name="icon-success" className={styles.iconTick} />
          <span className={styles.accountClosed}>Account closed</span>
        </>
      ) : (
        <>
          <strong>
            <CurrencyFormat value={loanBalance} />
          </strong>{" "}
          <span>outstanding</span>
        </>
      )}
    </div>
  </div>
);

function PersonalLoanAccountSummary({ account }) {
  const theme = useTheme();
  const productsInfo = useProducts();
  const products = productsInfo.data ?? [];

  // TODO: change interface from snake_case to camelCase
  const {
    account_number: accountNumber,
    loan_amount: loanAmount,
    loan_balance: loanBalance,
    payment_schedule: paymentSchedule,
    opened_on_date: openedOnDate,
    current_maturity_date: currentMaturityDate,
    current_interest_rate: currentInterestRate,
    country_code: countryCode,
    is_in_arrears: isInArrears,
    honeymoon_period: honeymoonPeriod,
    nextPaymentDay,
    nextPaymentDate,
    accountClosed,
    percentagePaid,
    loanTerm,
    isFinalPayment,
  } = account;
  const startDate = openedOnDate
    ? format(new Date(openedOnDate), "d MMM yyyy")
    : "";
  const maturityDate = currentMaturityDate
    ? format(new Date(currentMaturityDate), "d MMM yyyy")
    : "";

  const showInfo = !accountClosed && !isFinalPayment;
  const product = products.find((item) => item.id === account.product_id) || {};

  return (
    <div className={styles.wrapper}>
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          padding: theme.space[4],
          backgroundColor: theme.getColor("primary.blue.t100"),
          color: theme.getColor("white"),
        }}
      >
        <Text textStyle="heading5" color="white" margin="0 0 2 0">
          {getProductName(product)}
        </Text>
        {accountNumber && (
          <Text textStyle="body2" color="white" margin="auto 0 0 0">
            Account number {accountNumber}
          </Text>
        )}
      </div>
      {isInArrears && !accountClosed && (
        <RepaymentNotReceived countryCode={countryCode} />
      )}
      {isFinalPayment && (
        <FinalPayment countryCode={countryCode} date={nextPaymentDay} />
      )}
      <div className={styles.content}>
        <div className={styles.leftPanel}>
          <Progress
            percentage={percentagePaid}
            accountClosed={accountClosed}
            loanBalance={loanBalance}
          />
          {showInfo && (
            <>
              <div className={`body02 ${styles.info}`}>
                Information is updated daily at midnight. Payments may take 3
                days to clear.
              </div>
              <div
                className={`${styles.dataRows} ${
                  honeymoonPeriod ? styles.highlightBlock : ""
                }`}
              >
                {honeymoonPeriod ? (
                  <>
                    <div className={styles.interestRate}>
                      <strong>
                        {honeymoonPeriod.interest_rate.toFixed(2)}%
                      </strong>{" "}
                      <span className={styles.interestRateInfo}>
                        <span>interest rate for the</span>{" "}
                        <span>
                          first{" "}
                          {honeymoonPeriod.months > 1
                            ? `${honeymoonPeriod.months} months`
                            : `month`}
                        </span>
                      </span>
                    </div>
                    <div>
                      1st repayment due <strong>{nextPaymentDate}</strong>
                    </div>
                  </>
                ) : (
                  <div>
                    Repayment due <strong>{nextPaymentDay} of the month</strong>
                  </div>
                )}

                <div>
                  Monthly repayment{" "}
                  <strong>
                    <CurrencyFormat
                      value={paymentSchedule.monthly_minimum_payment_amount}
                    />
                    *
                  </strong>
                </div>
              </div>
            </>
          )}
        </div>
        <div className={styles.rightPanel}>
          <strong className={styles.heading}>Personal Loan Details</strong>
          <div className={styles.dataRows}>
            <div>
              Amount borrowed{" "}
              <strong>
                <CurrencyFormat value={loanAmount} />
              </strong>
            </div>
            <div>
              Start date <strong>{startDate}</strong>
            </div>
            <div>
              Maturity date <strong>{maturityDate}</strong>
            </div>
            <div>
              Loan term <strong>{loanTerm}</strong>
            </div>
            <div>
              Interest rate{" "}
              <strong>{currentInterestRate.toFixed(2)}% p.a.</strong>
            </div>
          </div>
          {showInfo && <RequestPayout countryCode={countryCode} />}
        </div>
      </div>
    </div>
  );
}

export { PersonalLoanAccountSummary };
